import React, { useState, useEffect } from 'react'

// Importa las funciones de tu BL (ajusta la ruta según tu estructura)
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from '../../utilities/BL/Zones'

// Importa el componente visual hijo (el que tiene los estilos y animación)
import { Messages as MessagesOverlay } from './Overlays/Messages'
import { FanMessages as FanMessagesOverlay } from './Overlays/FanMessages'


/**
 * COMPONENTE: MESSAGES OVERLAY (PARENT CONTAINER)
 * ------------------------------------------------------------------
 * Este componente es el "Cerebro" del Overlay. 
 * 1. Establece la conexión WebSocket como "overlay:messages".
 * 2. Escucha los mensajes entrantes (Messages y FanMessages).
 * 3. Gestiona la interrupción mutua entre dinámicas y cintillos.
 * 4. Pasa la información a <MessagesOverlay /> y <FanMessagesOverlay />.
 * ------------------------------------------------------------------
 */
const Messages = () => {
    
    // --- ESTADOS DE CONEXIÓN Y DATOS ---
    const [messagesData, setMessagesData] = useState(null); // Payload para el hijo Messages
    const [fanMessagesData, setFanMessagesData] = useState(null); // Payload para el hijo FanMessages
    const [connectionStatus, setConnectionStatus] = useState('idle');
    
    // -------------------------------------------------------------------------
    // EFECTO: FORZAR FONDO TRANSPARENTE
    // -------------------------------------------------------------------------
    useEffect(() => {
        // 1. Guardamos el color original por si acaso (buena práctica)
        const originalBodyStyle = document.body.style.backgroundColor;
        const originalHtmlStyle = document.documentElement.style.backgroundColor;
        const rootElement = document.getElementById('root'); // El contenedor de React

        // 2. Aplicamos la transparencia "a la fuerza"
        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        // 3. Cleanup: Cuando este componente se desmonte (cambies de pantalla),
        // restauramos el color original (o lo dejamos vacío para que tome el CSS).
        return () => {
            document.body.style.backgroundColor = originalBodyStyle;
            document.documentElement.style.backgroundColor = originalHtmlStyle;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
            }
        };
    }, []); // El array vacío asegura que esto corra solo al montar y desmontar

    // -------------------------------------------------------------------------
    // CICLO DE VIDA: CONEXIÓN Y DESCONEXIÓN
    // -------------------------------------------------------------------------
    useEffect(() => {
        // 1. Definir el manejador de mensajes (El "Switch")
        const handleIncomingMessage = (message) => {
            if (!message || !message.type) return;

            const msgType = message.type.toLowerCase();

            // 1. Mensajes de apoyo tradicionales
            if (msgType === 'messages') {
                const payload = message.data;
                // Si se proyecta un mensaje de apoyo, ocultamos el cintillo de FanMessages (interrupción mutua)
                if (payload && (payload.message || payload.action === 'show' || payload.action === 'show_lyric')) {
                    setFanMessagesData({ action: 'hide' });
                }
                setMessagesData(payload);
            }

            // 2. Mensajes a la afición (FanMessages)
            if (msgType === 'fanmessages') {
                const payload = message.data || message;
                const target = message.targetOverlay || payload.targetOverlay || 'all';

                // Verificamos si este evento está dirigido a messages o broadcast global
                if (target === 'messages' || target === 'all') {
                    const action = (message.action || payload.action || '').toLowerCase();
                    const isShow = action === 'show' || message.isVisible === true || payload.isVisible === true;
                    const isHide = action === 'hide' || message.isVisible === false || payload.isVisible === false;

                    // Si entra un mensaje a la afición, pausamos/ocultamos el mensaje de apoyo activo
                    if (isShow && !isHide) {
                        setMessagesData({ action: 'hide' });
                    }
                    setFanMessagesData(message);
                }
            }
        };

        // 2. Asignar el manejador de mensajes de inmediato
        SetSocketMessageHandler(handleIncomingMessage);

        // 3. Iniciar conexión WebSocket
        ConnectWebSocketForOverlay(
            'messages', 
            (status) => setConnectionStatus(status),
            (msg) => console.log('[Overlay] Toast:', msg)
        );

        // 4. Cleanup al desmontar
        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    // -------------------------------------------------------------------------
    // RENDERIZADO
    // -------------------------------------------------------------------------
    return (
        <>
            {/* Renderizamos el componente visual pasándole la data del socket */}
            <MessagesOverlay data={messagesData} />
            <FanMessagesOverlay data={fanMessagesData} />

            {/* Indicador de error de conexión (útil para debug en OBS) */}
            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'red', color: 'white', padding: 5, fontSize: 10 }}>D</div>
            )}
        </>
    );
};

export { Messages };